import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ResetIntegrationService } from '../../core/services/reset-integration.service';
import { CustomClaimService } from '../../core/services/custom-claim.service';
import {
  ResetCategories,
  PreviewResponse,
  ExecuteResponse,
} from '../../core/models/reset-integration.model';
import { environment } from '../../../environments/environment';

/**
 * Wizard states for the reset integration flow
 */
type WizardState = 'selection' | 'preview' | 'confirm' | 'progress' | 'results';

/**
 * Progress tracking for individual categories
 */
interface CategoryProgress {
  current: number;
  total: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
}

/**
 * Reset Integration Component
 *
 * Main page for the reset integration data feature. Manages a wizard flow
 * through selection, preview, confirmation, progress, and results states.
 *
 * Only accessible to users with superAdmin claim.
 *
 * @see agent-os/specs/2026-01-25-reset-integration-data/spec.md
 */
@Component({
  selector: 'app-reset-integration',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reset-integration.component.html',
  styleUrl: './reset-integration.component.scss',
})
export class ResetIntegrationComponent implements OnInit {
  // Wizard state
  wizardState: WizardState = 'selection';

  // Environment
  isProduction = environment.production;

  // Category selection
  categories: ResetCategories = this.createDefaultCategories();

  // Preview data
  previewData: PreviewResponse | null = null;
  previewLoading = false;
  previewError = '';

  // Confirmation
  confirmationPhrase = '';
  readonly requiredPhrase = 'RESET INTEGRATION DATA';

  // Progress tracking
  progressMap: Map<string, CategoryProgress> = new Map();
  currentOperation = '';
  errorCount = 0;
  isExecuting = false;

  // Results
  executeResults: ExecuteResponse | null = null;
  executeError = '';

  // Loading states
  loading = false;

  constructor(
    private router: Router,
    private resetService: ResetIntegrationService,
    private customClaimService: CustomClaimService
  ) {}

  ngOnInit(): void {
    // Verify super admin access
    if (!this.customClaimService.isSuperAdmin()) {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Create default categories with all options set to false
   */
  private createDefaultCategories(): ResetCategories {
    return {
      zohoFsm: {
        drones: false,
        serviceAppointments: false,
        workOrders: false,
        organizations: false,
        contacts: false,
      },
      zohoDesk: {
        supportTickets: false,
        organizations: false,
        contacts: false,
        drones: false,
      },
      firebase: {
        users: false,
      },
    };
  }

  // ============================================
  // CATEGORY SELECTION
  // ============================================

  /**
   * Toggle a category selection
   */
  toggleCategory(
    integration: 'zohoFsm' | 'zohoDesk' | 'firebase',
    category: string
  ): void {
    // Handle each integration type explicitly to satisfy TypeScript
    if (integration === 'zohoFsm') {
      const key = category as keyof typeof this.categories.zohoFsm;
      this.categories.zohoFsm[key] = !this.categories.zohoFsm[key];
    } else if (integration === 'zohoDesk') {
      const key = category as keyof typeof this.categories.zohoDesk;
      this.categories.zohoDesk[key] = !this.categories.zohoDesk[key];
    } else if (integration === 'firebase') {
      const key = category as keyof typeof this.categories.firebase;
      this.categories.firebase[key] = !this.categories.firebase[key];
    }
  }

  /**
   * Select all categories in a section
   */
  selectAllInSection(integration: 'zohoFsm' | 'zohoDesk' | 'firebase'): void {
    if (integration === 'zohoFsm') {
      this.categories.zohoFsm.drones = true;
      this.categories.zohoFsm.serviceAppointments = true;
      this.categories.zohoFsm.workOrders = true;
      this.categories.zohoFsm.organizations = true;
      this.categories.zohoFsm.contacts = true;
    } else if (integration === 'zohoDesk') {
      this.categories.zohoDesk.supportTickets = true;
      this.categories.zohoDesk.organizations = true;
      this.categories.zohoDesk.contacts = true;
      this.categories.zohoDesk.drones = true;
    } else if (integration === 'firebase') {
      this.categories.firebase.users = true;
    }
  }

  /**
   * Deselect all categories in a section
   */
  deselectAllInSection(integration: 'zohoFsm' | 'zohoDesk' | 'firebase'): void {
    if (integration === 'zohoFsm') {
      this.categories.zohoFsm.drones = false;
      this.categories.zohoFsm.serviceAppointments = false;
      this.categories.zohoFsm.workOrders = false;
      this.categories.zohoFsm.organizations = false;
      this.categories.zohoFsm.contacts = false;
    } else if (integration === 'zohoDesk') {
      this.categories.zohoDesk.supportTickets = false;
      this.categories.zohoDesk.organizations = false;
      this.categories.zohoDesk.contacts = false;
      this.categories.zohoDesk.drones = false;
    } else if (integration === 'firebase') {
      this.categories.firebase.users = false;
    }
  }

  /**
   * Check if any category is selected
   */
  hasAnySelection(): boolean {
    const { zohoFsm, zohoDesk, firebase } = this.categories;
    return (
      Object.values(zohoFsm).some((v) => v) ||
      Object.values(zohoDesk).some((v) => v) ||
      Object.values(firebase).some((v) => v)
    );
  }

  /**
   * Get count of selected categories
   */
  getSelectedCount(): number {
    const { zohoFsm, zohoDesk, firebase } = this.categories;
    return (
      Object.values(zohoFsm).filter((v) => v).length +
      Object.values(zohoDesk).filter((v) => v).length +
      Object.values(firebase).filter((v) => v).length
    );
  }

  /**
   * Check if all categories in a section are selected
   */
  isAllSelectedInSection(integration: 'zohoFsm' | 'zohoDesk' | 'firebase'): boolean {
    const section = this.categories[integration];
    return Object.values(section).every((v) => v);
  }

  /**
   * Check if any category in a section is selected
   */
  hasSelectionInSection(integration: 'zohoFsm' | 'zohoDesk' | 'firebase'): boolean {
    const section = this.categories[integration];
    return Object.values(section).some((v) => v);
  }

  // ============================================
  // WIZARD NAVIGATION
  // ============================================

  /**
   * Go to preview step
   */
  async goToPreview(): Promise<void> {
    if (!this.hasAnySelection()) {
      return;
    }

    this.previewLoading = true;
    this.previewError = '';

    try {
      this.previewData = await this.resetService.preview(this.categories);
      this.wizardState = 'preview';
    } catch (error: unknown) {
      console.error('Preview failed:', error);
      this.previewError = error instanceof Error ? error.message : 'Failed to generate preview';
    } finally {
      this.previewLoading = false;
    }
  }

  /**
   * Go back to selection step
   */
  goToSelection(): void {
    this.wizardState = 'selection';
    this.previewError = '';
  }

  /**
   * Go to confirmation step
   */
  goToConfirmation(): void {
    this.confirmationPhrase = '';
    this.wizardState = 'confirm';
  }

  /**
   * Go back to preview step
   */
  goBackToPreview(): void {
    this.confirmationPhrase = '';
    this.wizardState = 'preview';
  }

  /**
   * Cancel and return to dashboard
   */
  cancel(): void {
    this.router.navigate(['/dashboard']);
  }

  // ============================================
  // CONFIRMATION
  // ============================================

  /**
   * Check if confirmation phrase is valid
   */
  isConfirmationValid(): boolean {
    return this.confirmationPhrase === this.requiredPhrase;
  }

  /**
   * Get confirmation validation state
   */
  getConfirmationState(): 'empty' | 'invalid' | 'valid' {
    if (!this.confirmationPhrase || this.confirmationPhrase.trim() === '') {
      return 'empty';
    }
    return this.isConfirmationValid() ? 'valid' : 'invalid';
  }

  // ============================================
  // EXECUTION
  // ============================================

  /**
   * Execute the reset operation
   */
  async executeReset(): Promise<void> {
    if (!this.isConfirmationValid()) {
      return;
    }

    this.isExecuting = true;
    this.executeError = '';
    this.errorCount = 0;
    this.wizardState = 'progress';

    // Initialize progress tracking
    this.initializeProgress();

    try {
      this.executeResults = await this.resetService.execute(
        this.categories,
        this.confirmationPhrase
      );

      // Update progress to complete
      this.updateProgressFromResults(this.executeResults);

      this.wizardState = 'results';
    } catch (error: unknown) {
      console.error('Execute failed:', error);
      this.executeError = error instanceof Error ? error.message : 'Failed to execute reset operation';
      this.wizardState = 'results';
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Initialize progress tracking based on selected categories
   */
  private initializeProgress(): void {
    this.progressMap.clear();

    // Zoho FSM categories
    if (this.categories.zohoFsm.drones) {
      this.progressMap.set('zohoFsm_drones', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoFsm.serviceAppointments) {
      this.progressMap.set('zohoFsm_serviceAppointments', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoFsm.workOrders) {
      this.progressMap.set('zohoFsm_workOrders', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoFsm.organizations) {
      this.progressMap.set('zohoFsm_organizations', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoFsm.contacts) {
      this.progressMap.set('zohoFsm_contacts', { current: 0, total: 0, status: 'pending' });
    }

    // Zoho Desk categories
    if (this.categories.zohoDesk.supportTickets) {
      this.progressMap.set('zohoDesk_supportTickets', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoDesk.organizations) {
      this.progressMap.set('zohoDesk_organizations', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoDesk.contacts) {
      this.progressMap.set('zohoDesk_contacts', { current: 0, total: 0, status: 'pending' });
    }
    if (this.categories.zohoDesk.drones) {
      this.progressMap.set('zohoDesk_drones', { current: 0, total: 0, status: 'pending' });
    }

    // Firebase categories
    if (this.categories.firebase.users) {
      this.progressMap.set('firebase_users', { current: 0, total: 0, status: 'pending' });
    }

    // Set totals from preview if available
    if (this.previewData) {
      this.setProgressTotalsFromPreview();
    }
  }

  /**
   * Set progress totals from preview data
   */
  private setProgressTotalsFromPreview(): void {
    if (!this.previewData) return;

    const { zohoFsm, zohoDesk, firebase } = this.previewData.preview;

    // Zoho FSM
    if (zohoFsm?.drones) {
      const p = this.progressMap.get('zohoFsm_drones');
      if (p) p.total = zohoFsm.drones.toDelete;
    }
    if (zohoFsm?.serviceAppointments) {
      const p = this.progressMap.get('zohoFsm_serviceAppointments');
      if (p) p.total = zohoFsm.serviceAppointments.toDelete;
    }
    if (zohoFsm?.workOrders) {
      const p = this.progressMap.get('zohoFsm_workOrders');
      if (p) p.total = zohoFsm.workOrders.toDelete;
    }
    if (zohoFsm?.organizations) {
      const p = this.progressMap.get('zohoFsm_organizations');
      if (p) p.total = zohoFsm.organizations.toDelete;
    }
    if (zohoFsm?.contacts) {
      const p = this.progressMap.get('zohoFsm_contacts');
      if (p) p.total = zohoFsm.contacts.toDelete;
    }

    // Zoho Desk
    if (zohoDesk?.supportTickets) {
      const p = this.progressMap.get('zohoDesk_supportTickets');
      if (p) p.total = zohoDesk.supportTickets.toDelete;
    }
    if (zohoDesk?.organizations) {
      const p = this.progressMap.get('zohoDesk_organizations');
      if (p) p.total = zohoDesk.organizations.toDelete;
    }
    if (zohoDesk?.contacts) {
      const p = this.progressMap.get('zohoDesk_contacts');
      if (p) p.total = zohoDesk.contacts.toDelete;
    }
    if (zohoDesk?.drones) {
      const p = this.progressMap.get('zohoDesk_drones');
      if (p) p.total = zohoDesk.drones.toDelete;
    }

    // Firebase
    if (firebase?.users) {
      const p = this.progressMap.get('firebase_users');
      if (p) p.total = firebase.users.toDelete;
    }
  }

  /**
   * Update progress tracking from execution results
   */
  private updateProgressFromResults(results: ExecuteResponse): void {
    const { zohoFsm, zohoDesk, firebase } = results.results;

    // Zoho FSM
    if (zohoFsm?.drones) {
      const p = this.progressMap.get('zohoFsm_drones');
      if (p) {
        p.current = zohoFsm.drones.deleted;
        p.status = zohoFsm.drones.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoFsm?.serviceAppointments) {
      const p = this.progressMap.get('zohoFsm_serviceAppointments');
      if (p) {
        p.current = zohoFsm.serviceAppointments.deleted;
        p.status = zohoFsm.serviceAppointments.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoFsm?.workOrders) {
      const p = this.progressMap.get('zohoFsm_workOrders');
      if (p) {
        p.current = zohoFsm.workOrders.deleted;
        p.status = zohoFsm.workOrders.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoFsm?.organizations) {
      const p = this.progressMap.get('zohoFsm_organizations');
      if (p) {
        p.current = zohoFsm.organizations.deleted;
        p.status = zohoFsm.organizations.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoFsm?.contacts) {
      const p = this.progressMap.get('zohoFsm_contacts');
      if (p) {
        p.current = zohoFsm.contacts.deleted;
        p.status = zohoFsm.contacts.errors > 0 ? 'error' : 'completed';
      }
    }

    // Zoho Desk
    if (zohoDesk?.supportTickets) {
      const p = this.progressMap.get('zohoDesk_supportTickets');
      if (p) {
        p.current = zohoDesk.supportTickets.deleted;
        p.status = zohoDesk.supportTickets.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoDesk?.organizations) {
      const p = this.progressMap.get('zohoDesk_organizations');
      if (p) {
        p.current = zohoDesk.organizations.deleted;
        p.status = zohoDesk.organizations.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoDesk?.contacts) {
      const p = this.progressMap.get('zohoDesk_contacts');
      if (p) {
        p.current = zohoDesk.contacts.deleted;
        p.status = zohoDesk.contacts.errors > 0 ? 'error' : 'completed';
      }
    }
    if (zohoDesk?.drones) {
      const p = this.progressMap.get('zohoDesk_drones');
      if (p) {
        p.current = zohoDesk.drones.deleted;
        p.status = zohoDesk.drones.errors > 0 ? 'error' : 'completed';
      }
    }

    // Firebase
    if (firebase?.users) {
      const p = this.progressMap.get('firebase_users');
      if (p) {
        p.current = firebase.users.deleted;
        p.status = firebase.users.errors > 0 ? 'error' : 'completed';
      }
    }

    // Count errors
    this.errorCount = results.errors.length;
  }

  /**
   * Get overall progress percentage
   */
  getOverallProgress(): number {
    if (this.progressMap.size === 0) return 0;

    let totalProcessed = 0;
    let totalItems = 0;

    this.progressMap.forEach((progress) => {
      totalProcessed += progress.current;
      totalItems += progress.total;
    });

    return totalItems > 0 ? Math.round((totalProcessed / totalItems) * 100) : 0;
  }

  /**
   * Get progress entries as array for template
   */
  getProgressEntries(): { key: string; value: CategoryProgress }[] {
    const entries: { key: string; value: CategoryProgress }[] = [];
    this.progressMap.forEach((value, key) => {
      entries.push({ key, value });
    });
    return entries;
  }

  // ============================================
  // RESULTS
  // ============================================

  /**
   * Get total deleted count from results
   */
  getTotalDeleted(): number {
    if (!this.executeResults) return 0;

    const { zohoFsm, zohoDesk, firebase } = this.executeResults.results;
    let total = 0;

    if (zohoFsm) {
      if (zohoFsm.drones) total += zohoFsm.drones.deleted;
      if (zohoFsm.serviceAppointments) total += zohoFsm.serviceAppointments.deleted;
      if (zohoFsm.workOrders) total += zohoFsm.workOrders.deleted;
      if (zohoFsm.organizations) total += zohoFsm.organizations.deleted;
      if (zohoFsm.contacts) total += zohoFsm.contacts.deleted;
    }

    if (zohoDesk) {
      if (zohoDesk.supportTickets) total += zohoDesk.supportTickets.deleted;
      if (zohoDesk.organizations) total += zohoDesk.organizations.deleted;
      if (zohoDesk.contacts) total += zohoDesk.contacts.deleted;
      if (zohoDesk.drones) total += zohoDesk.drones.deleted;
    }

    if (firebase?.users) {
      total += firebase.users.deleted;
    }

    return total;
  }

  /**
   * Get total skipped count from results
   */
  getTotalSkipped(): number {
    if (!this.executeResults) return 0;

    const { zohoFsm, zohoDesk, firebase } = this.executeResults.results;
    let total = 0;

    if (zohoFsm) {
      if (zohoFsm.drones) total += zohoFsm.drones.skipped;
      if (zohoFsm.serviceAppointments) total += zohoFsm.serviceAppointments.skipped;
      if (zohoFsm.workOrders) total += zohoFsm.workOrders.skipped;
      if (zohoFsm.organizations) total += zohoFsm.organizations.skipped;
      if (zohoFsm.contacts) total += zohoFsm.contacts.skipped;
    }

    if (zohoDesk) {
      if (zohoDesk.supportTickets) total += zohoDesk.supportTickets.skipped;
      if (zohoDesk.organizations) total += zohoDesk.organizations.skipped;
      if (zohoDesk.contacts) total += zohoDesk.contacts.skipped;
      if (zohoDesk.drones) total += zohoDesk.drones.skipped;
    }

    if (firebase?.users) {
      total += firebase.users.skipped;
    }

    return total;
  }

  /**
   * Get total error count from results
   */
  getTotalErrors(): number {
    if (!this.executeResults) return 0;

    const { zohoFsm, zohoDesk, firebase } = this.executeResults.results;
    let total = 0;

    if (zohoFsm) {
      if (zohoFsm.drones) total += zohoFsm.drones.errors;
      if (zohoFsm.serviceAppointments) total += zohoFsm.serviceAppointments.errors;
      if (zohoFsm.workOrders) total += zohoFsm.workOrders.errors;
      if (zohoFsm.organizations) total += zohoFsm.organizations.errors;
      if (zohoFsm.contacts) total += zohoFsm.contacts.errors;
    }

    if (zohoDesk) {
      if (zohoDesk.supportTickets) total += zohoDesk.supportTickets.errors;
      if (zohoDesk.organizations) total += zohoDesk.organizations.errors;
      if (zohoDesk.contacts) total += zohoDesk.contacts.errors;
      if (zohoDesk.drones) total += zohoDesk.drones.errors;
    }

    if (firebase?.users) {
      total += firebase.users.errors;
    }

    return total;
  }

  /**
   * Check if operation completed successfully
   */
  isSuccess(): boolean {
    return this.executeResults?.status === 'completed' && this.getTotalErrors() === 0;
  }

  /**
   * Check if operation had partial failures
   */
  isPartialFailure(): boolean {
    return this.executeResults?.status === 'partial_failure' || this.getTotalErrors() > 0;
  }

  /**
   * Navigate to history page
   */
  viewHistory(): void {
    this.router.navigate(['/system/reset-integration/history']);
  }

  /**
   * Return to dashboard
   */
  returnToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  // ============================================
  // DISPLAY HELPERS
  // ============================================

  /**
   * Format category key for display
   */
  formatCategoryKey(key: string): string {
    const labels: Record<string, string> = {
      zohoFsm_drones: 'Zoho FSM - Drones',
      zohoFsm_serviceAppointments: 'Zoho FSM - Service Appointments',
      zohoFsm_workOrders: 'Zoho FSM - Work Orders',
      zohoFsm_organizations: 'Zoho FSM - Organizations',
      zohoFsm_contacts: 'Zoho FSM - Contacts',
      zohoDesk_supportTickets: 'Zoho Desk - Support Tickets',
      zohoDesk_organizations: 'Zoho Desk - Organizations',
      zohoDesk_contacts: 'Zoho Desk - Contacts',
      zohoDesk_drones: 'Zoho Desk - Drones',
      firebase_users: 'Firebase - Users',
    };
    return labels[key] || key;
  }

  /**
   * Format estimated duration for display
   */
  formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${seconds} seconds`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  }

  /**
   * Get list of selected categories for display
   */
  getSelectedCategories(): string[] {
    const selected: string[] = [];

    if (this.categories.zohoFsm.drones) selected.push('Zoho FSM - Drones');
    if (this.categories.zohoFsm.serviceAppointments) selected.push('Zoho FSM - Service Appointments');
    if (this.categories.zohoFsm.workOrders) selected.push('Zoho FSM - Work Orders');
    if (this.categories.zohoFsm.organizations) selected.push('Zoho FSM - Organizations');
    if (this.categories.zohoFsm.contacts) selected.push('Zoho FSM - Contacts');

    if (this.categories.zohoDesk.supportTickets) selected.push('Zoho Desk - Support Tickets');
    if (this.categories.zohoDesk.organizations) selected.push('Zoho Desk - Organizations');
    if (this.categories.zohoDesk.contacts) selected.push('Zoho Desk - Contacts');
    if (this.categories.zohoDesk.drones) selected.push('Zoho Desk - Drones');

    if (this.categories.firebase.users) selected.push('Firebase - Users');

    return selected;
  }
}
